import dayjs, { Dayjs } from 'dayjs';
import type {
  CalendarScheduleFrom,
  ChatAdjustmentMealContent,
  MealSuggestionPayload,
  ScheduledAction,
} from '@demind-inc/core';

import { ChatSuggestedEvent, UnifiedChatRichContext } from '../types';
import { ScheduleEvent } from '../api/science/openapi';

/** OpenAPI ScheduleEvent.rich_context and core ScheduledAction.richContext are
 * both looser than UnifiedChatRichContext (content may be an array, and
 * payload unions don't line up). Callers already read these fields loosely. */
const toUnifiedChatRichContext = (
  richContext: ScheduleEvent['rich_context'] | ScheduledAction['richContext']
): UnifiedChatRichContext | undefined => {
  if (!richContext) {
    return undefined;
  }
  return richContext as unknown as UnifiedChatRichContext;
};

/** Shared by both mappers below: pulls meal detail out of a schedule item's
 * (or ScheduledAction's) richContext.content, whichever of the two shapes
 * it's carried in, into the ChatAdjustmentMealContent AssistantChatSuggestedEventBlock reads. */
const extractMealContentFromRichContext = (
  richContext: ScheduleEvent['rich_context'] | ScheduledAction['richContext']
): ChatAdjustmentMealContent | undefined => {
  const content = Array.isArray(richContext?.content)
    ? richContext.content[0]
    : richContext?.content;
  if (content?.type !== 'meal_suggestion') {
    return undefined;
  }
  // Content.payload is a plain ContentPayload union, not discriminated by
  // Content.type at the type level, so narrowing needs an explicit cast here.
  const mealPayload = content.payload as MealSuggestionPayload;

  return {
    title: mealPayload.title,
    foodItems: mealPayload.food_items,
    macros: mealPayload.macros,
    micros: mealPayload.micros,
  };
};

const resolveOriginKind = (
  eventFrom: string | null | undefined,
  hasTodo: boolean,
  hasRoutine: boolean
): 'todo' | 'routine' | undefined => {
  if (hasTodo || eventFrom === 'todo') {
    return 'todo';
  }
  if (hasRoutine || eventFrom === 'routine') {
    return 'routine';
  }
  return undefined;
};

const transformHHmmToTargetDate = (time: string, date: Dayjs): Date => {
  const [hour, minute] = time.split(':').map((v) => parseInt(v, 10));
  return date.set('hour', hour).set('minute', minute).toDate();
};

const transformHHmmToDateConsideringMidnight = (
  time: string,
  date: string,
  timezone: string
): Date => {
  const [hourRaw, minuteRaw] = time.split(':');
  const hour = parseInt(hourRaw, 10);
  const minute = parseInt(minuteRaw, 10);
  const isAfterMidnight = hour >= 0 && hour < 16;

  if (isAfterMidnight) {
    const nextDate = dayjs(date).add(1, 'day').format('YYYY-MM-DD');
    const dateStr = `${nextDate}T${hour.toString().padStart(2, '0')}:${minute
      .toString()
      .padStart(2, '0')}:00`;
    return dayjs.tz(dateStr, timezone).toDate();
  }

  const dateStr = `${date}T${hour.toString().padStart(2, '0')}:${minute
    .toString()
    .padStart(2, '0')}:00`;
  return dayjs.tz(dateStr, timezone).toDate();
};

/**
 * Maps a "result_json" SSE payload's raw schedule items (same flat shape the
 * "rerun all plan" stream produces — meal/sleep/todo/calendar entries with
 * start_time/end_time/start_day_offset/rich_context etc., see
 * helpers/scheduler.helpers.ts's transformAllPlanV7ScheduleEventsToScheduleAction,
 * whose date/day-offset math this mirrors) into ChatSuggestedEvent[] so they
 * render inline in the chat thread via the existing AssistantChatSuggestedBlocks,
 * matching how a single suggested event already renders there.
 */
export const transformResultJsonSchedulesToSuggestedEvents = (
  schedules: ScheduleEvent[],
  date: string
): ChatSuggestedEvent[] => {
  const timezone = dayjs.tz.guess();

  return schedules
    .filter((schedule) => !!schedule.name)
    .map((schedule, index): ChatSuggestedEvent | null => {
      if (!schedule.start_time || !schedule.end_time || !schedule.name) {
        return null;
      }
      const isSleep = schedule.event_from === 'sleep';
      const hasDayOffsets = schedule.start_day_offset != null || schedule.end_day_offset != null;
      const startOffset = Number(schedule.start_day_offset ?? 0);
      const endOffset = Number(schedule.end_day_offset ?? 0);

      const startDate =
        isSleep && !hasDayOffsets
          ? transformHHmmToDateConsideringMidnight(schedule.start_time, date, timezone)
          : transformHHmmToTargetDate(
              schedule.start_time,
              dayjs(schedule.start_date || date, 'YYYY-MM-DD')
                .startOf('day')
                .add(startOffset, 'day')
            );

      let endDate =
        isSleep && !hasDayOffsets
          ? transformHHmmToDateConsideringMidnight(schedule.end_time, date, timezone)
          : transformHHmmToTargetDate(
              schedule.end_time,
              dayjs(schedule.end_date || date, 'YYYY-MM-DD')
                .startOf('day')
                .add(endOffset, 'day')
            );

      if (dayjs(endDate).isBefore(dayjs(startDate))) {
        endDate = dayjs(endDate).add(1, 'day').toDate();
      }

      // Prefer action_id so freeText / plan reschedules can reuse the same
      // temporary card via pending items (event_id is absent until committed).
      const eventId = schedule.action_id || schedule.event_id || `result-json-${index}`;

      // schedule.event_id, when present, means this adjustment targets an
      // already-committed calendar event (as opposed to a pure pending
      // suggestion that only has action_id) — e.g. "reschedule my workout".
      // Populating `before` here (even just the id — the raw adjustment only
      // carries the NEW start/end time, not the event's prior name/time, so a
      // full before/after diff isn't available without a separate calendar
      // lookup) lets AssistantChatSuggestedEventBlock choose "Update event"
      // over "Add event" wording via isNew below.
      const before = schedule.event_id ? { id: schedule.event_id } : undefined;
      const isRemove = schedule.schedule_action === 'remove';
      const isRoutine = schedule.event_from === 'routine';

      return {
        eventId,
        name: schedule.name,
        startTime: startDate.toISOString(),
        endTime: endDate.toISOString(),
        description: schedule.explanation ?? schedule.description ?? undefined,
        categoryId: schedule.category ?? undefined,
        mealContent: extractMealContentFromRichContext(schedule.rich_context),
        richContext: toUnifiedChatRichContext(schedule.rich_context),
        // Routine items use origin_routine_activity_id (the real routine
        // activity id) rather than eventId (an action_id/calendar event_id) —
        // routine accept/delete handlers key off this id to find the activity.
        autoScheduleFrom: schedule.event_from
          ? {
              from: schedule.event_from as CalendarScheduleFrom,
              id: (isRoutine && schedule.origin_routine_activity_id) || eventId,
              predicted: true,
            }
          : undefined,
        before,
        isNew: !before,
        // schedule_action: 'remove' on a routine-origin item must map to
        // 'deleteRoutine' (hides the recurring routine activity) rather than
        // 'deleteEvent', which would try to delete a single calendar
        // occurrence instead. Task (event_from: 'todo') removals stay
        // 'deleteEvent': ScheduleEvent only carries origin_todo_id, not the
        // boardId/appFrom a real task-board deletion needs, so the best
        // available action is deleting the task's calendar occurrence.
        action: isRemove ? (isRoutine ? 'deleteRoutine' : 'deleteEvent') : 'setEvent',
        originKind: resolveOriginKind(
          schedule.event_from,
          !!schedule.origin_todo_id,
          schedule.event_from === 'routine' || !!schedule.origin_routine_activity_id
        ),
      };
    })
    .filter((event): event is ChatSuggestedEvent => event !== null);
};

/**
 * Maps "Plan my day"/"Replan my day"'s ScheduledAction[] (the same schedules
 * useAllPlanSuggestedModal tracks — already resolved to ISO startDate/endDate,
 * no HH:mm/day-offset math needed) into ChatSuggestedEvent[], so those
 * results render via the same AssistantChatSuggestedBlocks UI as a "Replan"
 * result_json message. Matches RN's own mapper: every item — routine-origin
 * included — always renders via AssistantChatSuggestedEventBlock ('setEvent'),
 * never AssistantChatSuggestedRoutineBlock, so the CTA always reads "Add
 * event"/"Create event" here, regardless of where the schedule came from.
 */
export const transformScheduledActionsToSuggestedEvents = (
  schedules: ScheduledAction[]
): ChatSuggestedEvent[] => {
  const events = schedules.map((schedule, index) => {
    // eventId mirrors allPlanCache.helpers.ts's getScheduleId
    // (String(schedule.id ?? `plan-${index}`)) rather than importing it, since
    // that file pulls in localStorage/recoil-adjacent deps unsuited to this
    // lightweight mapper's other consumer (usePlanEntryHandler.ts) — keep the
    // two in sync if getScheduleId's fallback ever changes.
    const eventId = String(schedule.id ?? `plan-${index}`);

    return {
      eventId,
      name: schedule.name,
      startTime: schedule.startDate,
      endTime: schedule.endDate,
      description: schedule.description,
      categoryId: schedule.categoryId,
      mealContent: extractMealContentFromRichContext(schedule.richContext),
      richContext: toUnifiedChatRichContext(schedule.richContext),
      autoScheduleFrom: schedule.eventFrom
        ? {
            from: schedule.eventFrom as CalendarScheduleFrom,
            id: eventId,
            predicted: true,
          }
        : undefined,
      // activity-origin tasks/routines keep eventFrom as `activity`. The title
      // icon still needs todo vs routine (same rules as getPlanResultSource).
      originKind: resolveOriginKind(
        schedule.eventFrom,
        !!schedule.todoFrom || schedule.eventFrom === 'todo',
        !!schedule.routineFrom || schedule.eventFrom === 'routine'
      ),
      action: 'setEvent' as const,
    };
  });

  // Suggested items otherwise come back grouped by type (all sleep, all
  // tasks, ...), not by when they actually happen — show them in the order
  // they occur on the calendar instead, so e.g. a sleep block landing on the
  // next day sorts after that day's earlier items rather than up top.
  return events.sort((a, b) => dayjs(a.startTime).valueOf() - dayjs(b.startTime).valueOf());
};
